import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Card,
  Avatar,
  Chip,
  Button,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  CardContent,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Fab,
  Badge,
  Stack,
  CardMedia,
  CardActions,
  CardActionArea,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MessageIcon from "@mui/icons-material/Message";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InterestsIcon from "@mui/icons-material/Interests";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RecommendIcon from "@mui/icons-material/Recommend";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useEffect, useState } from "react";
import masterService from "../services/masterService";
import authService from "../services/authService";
import { useAuth } from "../context/AuthContext";
import RecommendedUsers from "./users/RecommendedUsers";
import PurchasedProfiles from "./users/PurchasedProfiles";

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // State for interests
  const [interestSent, setInterestSent] = useState([]);
  const [interestReceived, setInterestReceived] = useState([]);
  const [loadingInterests, setLoadingInterests] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for Sent, 1 for Received

  // State for main sections
  const [activeSection, setActiveSection] = useState(0); // 0: Recommended, 1: Purchased, 2: Interests

  // State for purchased profiles
  const [purchasedProfiles, setPurchasedProfiles] = useState([]);
  const [loadingPurchased, setLoadingPurchased] = useState(false);

  // State for subscription details
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // State for mobile drawer
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // State for current time
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());

  // Load purchased profiles
  const loadPurchasedProfiles = async () => {
    if (!currentUser?.id) return;
    setLoadingPurchased(true);
    try {
      const profiles = await masterService.getProfilePurchasesDetails();
      setPurchasedProfiles(profiles || []);
    } catch (error) {
      console.error("Error loading purchased profiles:", error);
      setPurchasedProfiles([]);
    } finally {
      setLoadingPurchased(false);
    }
  };

  // Load subscription details
  const loadSubscriptionDetails = async () => {
    if (!currentUser?.id) return;
    setSubscriptionLoading(true);
    try {
      const details = await authService.getUserSubscriptionDetails();
      setSubscriptionDetails(details);
    } catch (error) {
      console.error("Error loading subscription details:", error);
      setSubscriptionDetails(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Determine user's subscription access level (same logic as Search.jsx)
  const getUserSubscriptionAccess = () => {
    // If user is not logged in
    if (!currentUser) {
      return 'guest';
    }
    
    // If subscription is still loading
    if (subscriptionLoading) {
      return 'loading';
    }
    
    // If no subscription details available
    if (!subscriptionDetails || !subscriptionDetails.subscription_details) {
      return 'guest';
    }
    
    // Check plan_id for limited access
    const planId = subscriptionDetails.subscription_details.plan_id;
    
    if (planId == 1 || planId == 4) {
      return 'limited';
    }
    
    // All other plans have full access
    return 'full';
  };

  // Check if user has any subscription (for showing profile details vs purchase)
  const hasSubscription = () => {
    const access = getUserSubscriptionAccess();
    return access === 'limited' || access === 'full';
  };



 

  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [interests, setInterests] = useState(["Travel", "Cooking", "Music"]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingInterests(true);
      setLoadingRecommended(true);
      try {
        // Fetch recommended users
        const users = await masterService.getRecommendedUsers();
        setRecommendedUsers(users);
  
        // Fetch interest sent and received
        const [sentResponse, receivedResponse] = await Promise.all([
          masterService.getInterestSent(currentUser.id),
          masterService.getInterestReceived(currentUser.id),
        ]);
        console.log('Interest Sent Data:', sentResponse.data);
        console.log('Interest Received Data:', receivedResponse.data);
        setInterestSent(sentResponse.data || []);
        setInterestReceived(receivedResponse.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingInterests(false);
        setLoadingRecommended(false);
      }
    };
    if (currentUser?.id) {
      fetchData();
      loadPurchasedProfiles();
      loadSubscriptionDetails();
    }
  }, [currentUser?.id]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handler for heart click (like/unlike)
  const handleHeartClick = async (likedId) => {
    if (!currentUser?.id) return;
    try {
      const action = recommendedUsers.find((user) => user.id === likedId)
        ?.is_liked_by_me
        ? "unliked"
        : "liked";
      await masterService.likeUser(likedId, currentUser.id, action);
      setRecommendedUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === likedId
            ? {
                ...user,
                is_liked_by_me: !user.is_liked_by_me,
                liked_count: user.is_liked_by_me
                  ? (user.liked_count || 1) - 1
                  : (user.liked_count || 0) + 1,
              }
            : user
        )
      );
    } catch (error) {
      console.error("Error liking user:", error);
    }
  };

  // Handler for interested click
  const handleInterested = async (likedId) => {
    if (!currentUser?.id) return;
    try {
      const action = recommendedUsers.find((user) => user.id === likedId)
        ?.is_interested_by_me
        ? "not_interested"
        : "interested";
      await masterService.interestUser(likedId, currentUser.id, action);
      setRecommendedUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === likedId
            ? {
                ...user,
                is_interested_by_me: !user.is_interested_by_me,
              }
            : user
        )
      );
    } catch (error) {
      console.error("Error showing interest:", error);
    }
  };

  // Handler for accepting an interest
  const handleAcceptInterest = async (interestId, senderId) => {
    if (!currentUser?.id) return;
    try {
      await masterService.acceptInterest(interestId, currentUser.id, senderId);
      setInterestReceived((prev) =>
        prev.map((interest) =>
          interest.id === interestId
            ? { ...interest, status: "Accepted" }
            : interest
        )
      );
    } catch (error) {
      console.error("Error accepting interest:", error);
      alert("Failed to accept interest.");
    }
  };

  // Handler for rejecting an interest
  const handleRejectInterest = async (interestId, senderId) => {
    if (!currentUser?.id) return;
    try {
      await masterService.rejectInterest(interestId, currentUser.id, senderId);
      setInterestReceived((prev) =>
        prev.map((interest) =>
          interest.id === interestId
            ? { ...interest, status: "Rejected" }
            : interest
        )
      );
    } catch (error) {
      console.error("Error rejecting interest:", error);
      alert("Failed to reject interest.");
    }
  };



  const handlePurchase = async (receiverId) => {
    try {
      const payload = {
        purpose: "Plan Subscription single profile",
        amount: 200,
        name: currentUser.first_name + " " + currentUser.last_name,
        email: currentUser.email,
        phone: currentUser.mobile,
        user_unique_id: currentUser.unique_id,
        purchased_user_id: receiverId,
        redirect_type: "web",
      };

      const response = await masterService.createPayment(payload);
      if (response.status === "success") {
        window.location.href = response.payment_url; // redirect to Instamojo
      } else {
        alert("Failed to create payment.");
      }
    } catch (error) {
      console.error("Payment Error:", error);
      alert("Error creating payment.");
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      '& @keyframes pulse': {
        '0%': {
          opacity: 1,
          transform: 'scale(1)',
        },
        '50%': {
          opacity: 0.7,
          transform: 'scale(1.05)',
        },
        '100%': {
          opacity: 1,
          transform: 'scale(1)',
        },
      }
    }}>
      {/* Clean Top App Bar */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          color: theme.palette.text.primary,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          px: { xs: 2, md: 3 },
          py: 2,
          minHeight: '70px !important'
        }}>
          {/* Left Section - Dashboard Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ 
                  color: '#4a1e28',
                  '&:hover': { bgcolor: 'rgba(74,30,40,0.08)' }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#4a1e28',
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                letterSpacing: '-0.025em',
              }}
            >
              Dashboard
            </Typography>
          </Box>

          {/* Centered Welcome Message */}
          <Box sx={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5, 
                color: '#374151',
                fontSize: { xs: '1rem', md: '1.2rem' }
              }}
            >
              Welcome, {currentUser?.first_name || 'User'}!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: '#0d9488',
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
                fontSize: { xs: '0.8rem', md: '0.9rem' }
              }}
            >
              {currentTime}
            </Typography>
          </Box>

          {/* Right Section - Rating, Notifications, Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Rating */}
            <Box sx={{ textAlign: 'center', mr: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                Rating
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#facc15',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '1rem'
                }}
              >
                4.5 <StarIcon sx={{ fontSize: '1rem' }} />
              </Typography>
            </Box>

            {/* Notification Button */}
            <Badge 
              badgeContent={interestReceived.filter(i => i.status === 'pending').length} 
              color="error"
            >
              <IconButton
                sx={{
                  color: '#4a1e28',
                  '&:hover': { bgcolor: 'rgba(74,30,40,0.08)' }
                }}
              >
                <NotificationsIcon />
              </IconButton>
            </Badge>

            {/* Navigation Buttons */}
            <IconButton
              onClick={() => navigate("/")}
              sx={{ 
                color: '#4a1e28',
                '&:hover': { bgcolor: 'rgba(74,30,40,0.08)' }
              }}
            >
              <HomeIcon />
            </IconButton>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ 
                color: '#4a1e28',
                '&:hover': { bgcolor: 'rgba(74,30,40,0.08)' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: theme.palette.primary.main }}>
            Navigation
          </Typography>
          <List>
            {[
              { icon: <RecommendIcon />, label: 'Recommended', count: recommendedUsers.length },
              { icon: <ShoppingCartIcon />, label: 'Purchased', count: purchasedProfiles.length },
              { icon: <InterestsIcon />, label: 'Interests', count: interestSent.length + interestReceived.length },
            ].map((item, index) => (
              <ListItem
                key={index}
                button
                onClick={() => {
                  setActiveSection(index);
                  setMobileDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: activeSection === index ? theme.palette.primary.light + '20' : 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.primary.light + '10',
                  },
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  secondary={`${item.count} items`}
                  primaryTypographyProps={{ fontWeight: activeSection === index ? 600 : 400 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flex: 1, bgcolor: theme.palette.background.default }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          {/* Desktop Navigation Tabs */}
          {!isMobile && (
          <Paper 
            elevation={0} 
            sx={{ 
                mb: 4,
                borderRadius: 3,
              overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
              backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            }}
          >
            <Tabs
              value={activeSection}
              onChange={(e, newValue) => setActiveSection(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                    py: 2.5,
                    fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                    minHeight: 64,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                      background: 'rgba(0,0,0,0.04)',
                    },
                  },
                  '& .Mui-selected': {
                    color: theme.palette.primary.main,
                    background: 'rgba(0,0,0,0.04)',
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '2px 2px 0 0',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                },
              }}
            >
              <Tab
                  icon={<RecommendIcon />}
                iconPosition="start"
                  label={`Recommended (${recommendedUsers.length})`}
              />
              <Tab
                  icon={<ShoppingCartIcon />}
                iconPosition="start"
                  label={`Purchased (${purchasedProfiles.length})`}
              />
              <Tab
                  icon={<InterestsIcon />}
                iconPosition="start"
                  label={`Interests (${interestSent.length + interestReceived.length})`}
                />
              </Tabs>
            </Paper>
          )}

          {/* Mobile Section Selector */}
          {isMobile && (
            <Box sx={{ mb: 3 }}>
              <Paper 
                elevation={0}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  overflow: 'hidden',
                }}
              >
                <Tabs
                  value={activeSection}
                  onChange={(e, newValue) => setActiveSection(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      minWidth: 'auto',
                      px: 2,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                    },
                    '& .Mui-selected': {
                      color: theme.palette.primary.main,
                    },
                    '& .MuiTabs-indicator': {
                      height: 2,
                      borderRadius: '1px 1px 0 0',
                      background: theme.palette.primary.main,
                    },
                  }}
                >
                  <Tab label="Recommended" />
                  <Tab label="Purchased" />
                  <Tab label="Interests" />
            </Tabs>
          </Paper>
        </Box>
          )}

        {/* Section Content */}
        {activeSection === 0 && (
          <RecommendedUsers
            users={recommendedUsers}
            loading={loadingRecommended}
            onHeartClick={handleHeartClick}
            onInterested={handleInterested}
            onPurchase={handlePurchase}
            theme={theme}
            hasSubscription={hasSubscription()}
          />
        )}

        {activeSection === 1 && (
          <PurchasedProfiles theme={theme} currentUser={currentUser} />
        )}

        {activeSection === 2 && (
          <Box sx={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            }}>
              {/* Section Header */}
              <Box sx={{ 
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.light}10 100%)`,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
              }}>
              <Typography 
                  variant="h5" 
                sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                💕 Your Interests
              </Typography>
              <Typography 
                  variant="body1" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  opacity: 0.8
                }}
              >
                Manage your connections and interests
              </Typography>
            </Box>
            
              {/* Interest Tabs */}
              <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiTabs-indicator': {
                      height: 3,
                    borderRadius: '2px 2px 0 0',
                      background: theme.palette.primary.main,
                  },
                }}
              >
                  <Tab label={`Sent (${interestSent.length})`} />
                  <Tab label={`Received (${interestReceived.length})`} />
              </Tabs>
              </Box>

            {/* Interest Sent Tab */}
            {activeTab === 0 && (
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                {loadingInterests ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={40} />
                  </Box>
                ) : interestSent.length > 0 ? (
                    <Grid container spacing={2}>
                    {interestSent.map((interest) => (
                      <Grid item xs={12} sm={6} md={4} key={interest.id}>
                        <Card sx={{ 
                            p: 2.5,
                            borderRadius: 2,
                            background: 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.5)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flex: 1 }}>
                            <Avatar sx={{ 
                                width: 45, 
                                height: 45, 
                              mr: 2,
                              bgcolor: theme.palette.primary.main 
                            }}>
                              <PersonIcon />
                            </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '0.95rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    mb: 0.5
                                  }}
                                >
                                {interest.receiver_name || interest.name || interest.user_name || interest.first_name || 'Unknown User'}
                              </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                                  {new Date(interest.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                            <Box sx={{ mt: 'auto' }}>
                          <Chip 
                            label={interest.status} 
                            color={interest.status === 'accepted' ? 'success' : interest.status === 'rejected' ? 'error' : 'warning'}
                            size="small"
                                sx={{ fontSize: '0.75rem' }}
                          />
                            </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <InterestsIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2, opacity: 0.6 }} />
                      <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1, fontWeight: 500 }}>
                      No interests sent yet
                    </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, opacity: 0.7 }}>
                      Start showing interest in profiles you like
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Interest Received Tab */}
            {activeTab === 1 && (
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                {loadingInterests ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={40} />
                  </Box>
                ) : interestReceived.length > 0 ? (
                    <Grid container spacing={2}>
                    {interestReceived.map((interest) => (
                      <Grid item xs={12} sm={6} md={4} key={interest.id}>
                        <Card sx={{ 
                            p: 2.5,
                            borderRadius: 2,
                            background: 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.5)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                            }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flex: 1 }}>
                            <Avatar sx={{ 
                                width: 45, 
                                height: 45, 
                              mr: 2,
                              bgcolor: theme.palette.secondary?.main || theme.palette.primary.main 
                            }}>
                              <PersonIcon />
                            </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '0.95rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    mb: 0.5
                                  }}
                                >
                                {interest.sender_name || interest.name || interest.user_name || interest.first_name || 'Unknown User'}
                              </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                                  {new Date(interest.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                            <Box sx={{ mt: 'auto' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                            <Chip 
                              label={interest.status} 
                              color={interest.status === 'accepted' ? 'success' : interest.status === 'rejected' ? 'error' : 'warning'}
                              size="small"
                                  sx={{ fontSize: '0.75rem' }}
                            />
                            {interest.status === 'pending' && (
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={() => handleAcceptInterest(interest.id, interest.sender_id)}
                                      sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                                >
                                  Accept
                                </Button>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  startIcon={<CloseIcon />}
                                  onClick={() => handleRejectInterest(interest.id, interest.sender_id)}
                                      sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                              </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <InterestsIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2, opacity: 0.6 }} />
                      <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1, fontWeight: 500 }}>
                      No interests received yet
                    </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, opacity: 0.7 }}>
                      When someone shows interest in your profile, it will appear here
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;